/**
 * Simulation de joueurs (bots) pour le MODE TEST du Live School.
 *
 * Un vrai défi a des participants réels dont le player ne peut pas lire le
 * décompte (RLS `own_select`) → il poll la RPC `get_live_step_participation`.
 * En mode test (staff/dev, `?test=`), il n'y a qu'un seul participant réel : on
 * SIMULE `LIVE_BOT_COUNT` bots qui répondent à des instants aléatoires DANS la
 * fenêtre de l'étape, pour donner vie à l'affichage « tu es le N-ième » /
 * « X en attente » / résolution anticipée « tout le monde a répondu ».
 *
 * Helpers PURS (aucun effet, `rand` injectable) → testés unitairement ; le hook
 * fige les offsets par étape via `Math.random`.
 */

export const LIVE_BOT_COUNT = 3;

export interface BotPlanOptions {
  /** Délai minimal avant la 1re réponse d'un bot (ms depuis le début de l'étape). */
  minDelayMs: number;
  /** Étalement max des réponses des bots au-delà du délai minimal (ms). */
  maxSpanMs: number;
  /** Marge avant la fin de l'étape : aucun bot ne répond dans les dernières ms. */
  endBufferMs: number;
}

const DEFAULT_OPTS: BotPlanOptions = {
  minDelayMs: 1500,
  maxSpanMs: 20000,
  endBufferMs: 2500,
};

/**
 * Instants de réponse (ms depuis le début de l'étape) de `count` bots, triés
 * croissants et bornés dans `[lo, hi]` ⊂ `[0, stepDurationMs - endBufferMs]`.
 * `rand` ∈ [0,1) injectable (défaut `Math.random`).
 */
export function planBotOffsets(
  count: number,
  stepDurationMs: number,
  rand: () => number = Math.random,
  opts: Partial<BotPlanOptions> = {},
): number[] {
  const { minDelayMs, maxSpanMs, endBufferMs } = { ...DEFAULT_OPTS, ...opts };
  const ceiling = Math.max(0, stepDurationMs - endBufferMs);
  const lo = Math.min(minDelayMs, ceiling);
  const hi = Math.max(lo, Math.min(ceiling, minDelayMs + maxSpanMs));
  const offsets: number[] = [];
  for (let i = 0; i < Math.max(0, count); i++) {
    offsets.push(Math.round(lo + rand() * (hi - lo)));
  }
  return offsets.sort((a, b) => a - b);
}

/** Nombre de bots ayant répondu à `elapsedMs` (offset ≤ elapsed). */
export function countBotsAnswered(offsets: readonly number[], elapsedMs: number): number {
  let n = 0;
  for (const o of offsets) if (o <= elapsedMs) n++;
  return n;
}

/**
 * Mon rang parmi (bots + moi) si je réponds à `userElapsedMs` : nb de bots ayant
 * répondu STRICTEMENT avant moi + 1 (je suis au moins 1er).
 */
export function positionAmong(botOffsets: readonly number[], userElapsedMs: number): number {
  let before = 0;
  for (const o of botOffsets) if (o < userElapsedMs) before++;
  return before + 1;
}
